import { useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { PLANETS, gameReducer, initialState, getNextRiddle } from './gameReducer';
import { initEphemeris, parseDate, parseTime, calculateChart } from './ephemeris';
import { NARRATIVE, PLANET_DESCRIPTIONS, boundaryWarning, APPROXIMATE_WARNING, BES_INTRO, BES_RIDDLE_INTRO, BES_COMPLETION, THOTH_CONTEXT, SWAG_INTRO, SWAG_SCORE_MESSAGES } from './narrative';
import { calculateChaoticGoodScore } from './scoring';
import ExportPanel from './ExportPanel';
import { RIDDLES, checkAnswer } from './riddles';
import { longitudeToSign, isNearBoundary, SIGNS } from './chartMath';
import { findCity } from './cities';
import DeviceFrame from './DeviceFrame';
import GearBar from './GearBar';
import StarMapWheel from './StarMapWheel';
import RPGChat from './RPGChat';
import '../../styles/antikythera.css';

export default function AntikytheraDevice() {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const bootedRef = useRef(false);

  // ── Boot: preload WASM + send Bes intro messages ─────────────────────────
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    initEphemeris();
    // Start with Bes intro (new 'intro' phase)
    dispatch({
      type: 'ADD_MESSAGES',
      payload: BES_INTRO.map(text => ({ sender: 'bes', text })),
    });
    dispatch({
      type: 'ADD_MESSAGES',
      payload: [{ type: 'hint', text: '(press enter to wake the machine)' }],
    });
  }, []);

  // ── Helper: get Thoth context for a planet/sign ───────────────────────────
  const getThothMessage = useCallback((planetKey, signName) => {
    const ctx = THOTH_CONTEXT[planetKey];
    if (!ctx) return null;
    // Sun has per-sign context (object), others are a single string
    const text = typeof ctx === 'string' ? ctx : ctx[signName.toLowerCase()];
    if (!text) return null;
    return { sender: 'thoth', text };
  }, []);

  // ── Main input handler ────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (input) => {
    const echo = { sender: 'player', text: input };

    switch (state.phase) {

      // ── Intro phase (Bes) — press Enter to wake the machine ───────────
      case 'intro': {
        // User presses Enter with empty or any input to advance
        dispatch({
          type: 'ADD_MESSAGES',
          payload: NARRATIVE.boot.messages.map(text => ({ sender: 'device', text })),
        });
        dispatch({ type: 'SET_PHASE', payload: 'ask-birthday' });
        return;
      }

      // ── Ask Birthday ──────────────────────────────────────────────────
      case 'ask-birthday': {
        dispatch({ type: 'ADD_MESSAGES', payload: [echo] });

        const date = parseDate(input);
        if (!date) {
          dispatch({
            type: 'ADD_MESSAGES',
            payload: [{ sender: 'device', text: 'The gears do not recognize that date. Try again.' }],
          });
          return;
        }

        dispatch({ type: 'SET_BIRTH_DATE', payload: date });

        // Sun-only chart: noon, zero coords
        const chart = await calculateChart(date, 12, 0, 0);
        const sunLon = chart.planets.sun.longitude;
        const sunSign = longitudeToSign(sunLon);
        const sunDesc = PLANET_DESCRIPTIONS.sun[sunSign.sign.name.toLowerCase()];
        const sunPlanet = PLANETS.find(p => p.id === 'sun');

        const sunMessages = [
          { type: 'clunk', color: '#ffbd2e', text: 'CLUNK.' },
          { sender: 'device', text: `${sunPlanet.glyph} Sun in ${sunSign.sign.name}.` },
          { sender: 'device', text: sunDesc },
        ];

        // Thoth context for Sun sign
        const sunThoth = getThothMessage('sun', sunSign.sign.name);
        if (sunThoth) sunMessages.push(sunThoth);

        dispatch({ type: 'ADD_MESSAGES', payload: sunMessages });

        dispatch({ type: 'PLACE_PLANET', payload: { id: 'sun', longitude: sunLon } });
        dispatch({ type: 'SET_CHART_DATA', payload: chart });

        dispatch({
          type: 'ADD_MESSAGES',
          payload: NARRATIVE.askTime.messages.map(text => ({ sender: 'device', text })),
        });

        dispatch({ type: 'SET_PHASE', payload: 'ask-time' });
        return;
      }

      // ── Ask Time ──────────────────────────────────────────────────────
      case 'ask-time': {
        dispatch({ type: 'ADD_MESSAGES', payload: [echo] });

        const time = parseTime(input);
        if (!time) {
          dispatch({
            type: 'ADD_MESSAGES',
            payload: [{ sender: 'device', text: 'I could not read that time. A number, or a word like "morning".' }],
          });
          return;
        }

        dispatch({ type: 'SET_BIRTH_TIME', payload: { time: time.hours, approximate: time.approximate } });

        // Recalculate with actual time, still zero coords
        const chart = await calculateChart(state.birthDate, time.hours, 0, 0);
        const moonLon = chart.planets.moon.longitude;
        const moonSign = longitudeToSign(moonLon);
        const moonDesc = PLANET_DESCRIPTIONS.moon[moonSign.sign.name.toLowerCase()];
        const moonPlanet = PLANETS.find(p => p.id === 'moon');

        const messages = [
          { type: 'clunk', color: '#8c78ff', text: 'CLUNK.' },
          { sender: 'device', text: `${moonPlanet.glyph} Moon in ${moonSign.sign.name}.` },
          { sender: 'device', text: moonDesc },
        ];

        // Thoth context for Moon
        const moonThoth = getThothMessage('moon', moonSign.sign.name);
        if (moonThoth) messages.push(moonThoth);

        if (time.approximate) {
          messages.push({ sender: 'device', text: APPROXIMATE_WARNING });
        }

        if (isNearBoundary(moonLon)) {
          const moonSignIdx = moonSign.signIndex;
          const adjacentIdx = moonSign.degree < 1
            ? (moonSignIdx - 1 + 12) % 12
            : (moonSignIdx + 1) % 12;
          const adjacentSign = SIGNS[adjacentIdx];
          messages.push({
            sender: 'device',
            text: boundaryWarning('Moon', moonSign.sign.name, adjacentSign.name),
          });
        }

        dispatch({ type: 'ADD_MESSAGES', payload: messages });
        dispatch({ type: 'PLACE_PLANET', payload: { id: 'moon', longitude: moonLon } });

        dispatch({
          type: 'ADD_MESSAGES',
          payload: NARRATIVE.askLocation.messages.map(text => ({ sender: 'device', text })),
        });

        dispatch({ type: 'SET_PHASE', payload: 'ask-location' });
        return;
      }

      // ── Ask Location ──────────────────────────────────────────────────
      case 'ask-location': {
        dispatch({ type: 'ADD_MESSAGES', payload: [echo] });

        const city = findCity(input);
        if (!city) {
          dispatch({
            type: 'ADD_MESSAGES',
            payload: [{ sender: 'device', text: 'I do not know that place. Try a major city.' }],
          });
          return;
        }

        dispatch({ type: 'SET_BIRTH_LOCATION', payload: city });

        // Final chart with location
        const chart = await calculateChart(
          state.birthDate,
          state.birthTime ?? 12,
          city.lat,
          city.lon,
        );

        const ascLon = chart.planets.ascendant.longitude;
        const ascSign = longitudeToSign(ascLon);
        const ascDesc = PLANET_DESCRIPTIONS.asc[ascSign.sign.name.toLowerCase()];
        const ascPlanet = PLANETS.find(p => p.id === 'ascendant');

        const ascMessages = [
          { type: 'clunk', color: '#00ff41', text: 'CLUNK.' },
          { sender: 'device', text: `${ascPlanet.glyph} Ascendant in ${ascSign.sign.name}.` },
          { sender: 'device', text: ascDesc },
        ];

        // Thoth context for Ascendant
        const ascThoth = getThothMessage('ascendant', ascSign.sign.name);
        if (ascThoth) ascMessages.push(ascThoth);

        dispatch({ type: 'ADD_MESSAGES', payload: ascMessages });

        dispatch({ type: 'PLACE_PLANET', payload: { id: 'ascendant', longitude: ascLon } });
        dispatch({ type: 'SET_CHART_DATA', payload: chart });

        // Bes riddle intro
        dispatch({
          type: 'ADD_MESSAGES',
          payload: BES_RIDDLE_INTRO.map(text => ({ sender: 'bes', text })),
        });

        // Device riddle intro
        dispatch({
          type: 'ADD_MESSAGES',
          payload: NARRATIVE.riddleIntro.map(text => ({ sender: 'device', text })),
        });

        // First riddle
        const firstRiddle = 'mars';
        dispatch({ type: 'SET_CURRENT_RIDDLE', payload: firstRiddle });
        dispatch({
          type: 'ADD_MESSAGES',
          payload: [{ type: 'riddle', lines: RIDDLES[firstRiddle].lines }],
        });

        dispatch({ type: 'SET_PHASE', payload: 'riddle-mars' });
        return;
      }

      // ── Riddle phases ─────────────────────────────────────────────────
      case 'riddle-mars':
      case 'riddle-venus':
      case 'riddle-saturn':
      case 'riddle-jupiter': {
        dispatch({ type: 'ADD_MESSAGES', payload: [echo] });

        const riddleKey = state.currentRiddle;
        const { correct } = checkAnswer(riddleKey, input);

        if (!correct) {
          const hintIdx = state.riddleHintIndex[riddleKey] ?? 0;
          const hints = RIDDLES[riddleKey].hints;
          const hint = hints[Math.min(hintIdx, hints.length - 1)];
          dispatch({
            type: 'ADD_MESSAGES',
            payload: [
              { sender: 'device', text: 'Not quite. The gears resist.' },
              { sender: 'device', text: hint },
            ],
          });
          dispatch({ type: 'INCREMENT_HINT', payload: riddleKey });
          return;
        }

        // Correct answer — celebration + place the planet
        const planetLon = state.chartData.planets[riddleKey].longitude;
        const planetSign = longitudeToSign(planetLon);
        const descKey = riddleKey; // mars, venus, saturn, jupiter all match PLANET_DESCRIPTIONS keys
        const planetDesc = PLANET_DESCRIPTIONS[descKey][planetSign.sign.name.toLowerCase()];
        const planetInfo = PLANETS.find(p => p.id === riddleKey);

        const riddleMessages = [
          { type: 'celebration', text: 'GEAR ENGAGED' },
          { type: 'clunk', color: planetInfo.color, text: 'CLUNK.' },
          { sender: 'device', text: `${planetInfo.glyph} ${planetInfo.label} in ${planetSign.sign.name}.` },
          { sender: 'device', text: planetDesc },
        ];

        // Thoth context for the riddle planet
        const riddleThoth = getThothMessage(riddleKey, planetSign.sign.name);
        if (riddleThoth) riddleMessages.push(riddleThoth);

        dispatch({ type: 'ADD_MESSAGES', payload: riddleMessages });

        dispatch({ type: 'PLACE_PLANET', payload: { id: riddleKey, longitude: planetLon } });

        const nextRiddle = getNextRiddle(riddleKey);
        if (nextRiddle) {
          dispatch({ type: 'SET_CURRENT_RIDDLE', payload: nextRiddle });
          dispatch({
            type: 'ADD_MESSAGES',
            payload: [{ type: 'riddle', lines: RIDDLES[nextRiddle].lines }],
          });
          dispatch({ type: 'SET_PHASE', payload: `riddle-${nextRiddle}` });
        } else {
          // Bes completion
          dispatch({
            type: 'ADD_MESSAGES',
            payload: BES_COMPLETION.map(text => ({ sender: 'bes', text })),
          });
          dispatch({
            type: 'ADD_MESSAGES',
            payload: NARRATIVE.completion.messages.map(text => ({ sender: 'device', text })),
          });

          // Calculate Chaotic Good score
          const allPlaced = [...state.placedPlanets, {
            id: riddleKey,
            longitude: planetLon,
            sign: planetSign,
          }];
          const scoreResult = calculateChaoticGoodScore(allPlaced);
          dispatch({ type: 'SET_CHAOTIC_GOOD_SCORE', payload: scoreResult });

          // SWAG intro
          dispatch({
            type: 'ADD_MESSAGES',
            payload: SWAG_INTRO.map(text => ({ sender: 'swag', text })),
          });

          // SWAG score reveal
          dispatch({
            type: 'ADD_MESSAGES',
            payload: SWAG_SCORE_MESSAGES(scoreResult.score, scoreResult.reading)
              .map(text => ({ sender: 'swag', text })),
          });

          // Export panel message
          dispatch({
            type: 'ADD_MESSAGES',
            payload: [{ type: 'export-panel' }],
          });

          dispatch({ type: 'SET_PHASE', payload: 'export' });
        }
        return;
      }

      default:
        return;
    }
  }, [state.phase, state.birthDate, state.birthTime, state.chartData, state.currentRiddle, state.riddleHintIndex, state.placedPlanets, getThothMessage]);

  // ── Planet click handler (explore mode) ────────────────────────────────────
  const handlePlanetClick = useCallback((planetId) => {
    const placed = state.placedPlanets.find(p => p.id === planetId);
    if (!placed) return;

    const descKey = planetId === 'ascendant' ? 'asc' : planetId;
    const signName = placed.sign.sign.name.toLowerCase();
    const description = PLANET_DESCRIPTIONS[descKey]?.[signName];
    if (!description) return;

    const planetInfo = PLANETS.find(p => p.id === planetId);

    const exploreMessages = [
      { sender: 'device', text: `${planetInfo.glyph} ${planetInfo.label} in ${placed.sign.sign.name}.` },
      { sender: 'device', text: description },
    ];

    // Also show Thoth context in explore mode
    const thothKey = planetId === 'ascendant' ? 'ascendant' : planetId;
    const thothMsg = getThothMessage(thothKey, placed.sign.sign.name);
    if (thothMsg) exploreMessages.push(thothMsg);

    dispatch({ type: 'ADD_MESSAGES', payload: exploreMessages });
  }, [state.placedPlanets, getThothMessage]);

  // ── Ghost planets: show unplaced planets at their real longitude ────────────
  const ghostPlanets = useMemo(() => {
    if (!state.chartData) return [];
    const placedIds = new Set(state.placedPlanets.map(p => p.id));
    return PLANETS
      .filter(p => !placedIds.has(p.id) && state.chartData.planets[p.id])
      .map(p => ({
        id: p.id,
        glyph: p.glyph,
        longitude: state.chartData.planets[p.id].longitude,
      }));
  }, [state.chartData, state.placedPlanets]);

  // ── Hint text ──────────────────────────────────────────────────────────────
  const currentHint = useMemo(() => {
    switch (state.phase) {
      case 'intro':        return '(press enter to wake the machine)';
      case 'ask-birthday': return NARRATIVE.boot.hint;
      case 'ask-time':     return NARRATIVE.askTime.hint;
      case 'ask-location': return NARRATIVE.askLocation.hint;
      default:             return null;
    }
  }, [state.phase]);

  // ── Input disabled ─────────────────────────────────────────────────────────
  const isInputDisabled = state.phase === 'boot' || state.phase === 'complete' || state.phase === 'scoring' || state.phase === 'export' || state.phase === 'explore';
  const isExplorePhase = state.phase === 'complete' || state.phase === 'scoring' || state.phase === 'export' || state.phase === 'explore';

  // ── Export panel renderer ───────────────────────────────────────────────────
  const renderExportPanel = useCallback(() => (
    <ExportPanel
      messages={state.messages}
      score={state.chaoticGoodScore?.score ?? 0}
    />
  ), [state.messages, state.chaoticGoodScore]);

  return (
    <div className="antikythera-device">
      <DeviceFrame gearsUnlocked={state.gearsUnlocked}>
        <GearBar gearsUnlocked={state.gearsUnlocked} />
        <StarMapWheel
          placedPlanets={state.placedPlanets}
          aspects={state.aspects}
          gearsUnlocked={state.gearsUnlocked}
          ghostPlanets={ghostPlanets}
          onPlanetClick={isExplorePhase ? handlePlanetClick : undefined}
        />
        <RPGChat
          messages={state.messages}
          onSubmit={handleSubmit}
          inputDisabled={isInputDisabled}
          hintText={currentHint}
          allowEmptySubmit={state.phase === 'intro'}
          renderExportPanel={renderExportPanel}
          keyboardMode={state.phase === 'ask-birthday' ? 'date' : state.phase === 'ask-time' ? 'time' : 'text'}
        />
      </DeviceFrame>
    </div>
  );
}
